import React from "react";
import Title from "../../ui/Title";

const Overview = () => {
  return (
    <div id="overview-gender-caregiving" className="scroll-mt-24">
      <Title
        title="An overview of gender inequality in caregiving"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          Caregiving is deeply intertwined with gender inequality. Around the
          world, caregiving continues to be seen as women’s work due to existing
          gender norms, and a disproportionate amount of unpaid care work falls
          on women and girls.
        </p>
        <p>
          According to an{" "}
          <a
            href="https://jamanetwork.com/journals/jama-health-forum/fullarticle/2805890"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            article
          </a>{" "}
          on unpaid family caregiving, gender norms begin to be reinforced from
          childhood, and socio-cultural experiences like play and role-modeling
          strengthen the norms of “socializing women into caregivers.”
          Similarly, deeply held norms in society about masculinity also act as
          barriers for men to take up caregiving roles.
        </p>
        <p>
          According to the{" "}
          <a
            href="https://www.ilo.org/resource/news/ilo-women-do-4-times-more-unpaid-care-work-men-asia-and-pacific"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            report
          </a>{" "}
          published by the International Labour Organization (ILO) in 2018,
          found that across the world, women perform 76 percent of the total
          amount of unpaid care work, which is 3.2 times more than men. This
          inequality not only puts a high degree of load on women but also
          exacerbates disparity when it comes to career and personal growth.
          This unequal load of care work on women has significant negative
          effects on their physical and emotional well-being.
        </p>
        <p>
          In 2023 and 2024, The Caregiving Lab at Noora Health conducted
          research with family caregivers of patients suffering from high-risk
          or life-threatening illnesses. The stories we heard highlighted the
          gender disparity in care. Among the participants we interviewed, in
          all instances, women had to wear multiple hats — serving as a
          full-time caregiver for a patient, as well as a carer for the rest of
          the family members.
        </p>
        <p>
          From our research, we also learned that often, the role of a caregiver
          is defaulted to the women in the family. Familial expectations and a
          sense of responsibility leave women with no choice but to take up the
          significant load of caring for the family. In most cases, while male
          members of the family help in certain care tasks like lifting, and
          facilitating hospital visits, their support is often limited.
          Similarly, despite being primary caregivers, women often do not have
          much say in the treatment decisions of their dependents, as the male
          members of the family primarily make these decisions.
        </p>
        <p>
          Not only does gender disparity put an unequal load on women, but it
          also significantly affects their aspirations and limits their personal
          and career growth.
        </p>
        <p>
          Data from around the world on the impact of care work on workforce
          participation shows an alarming situation. According to{" "}
          <a
            href="https://www.ilo.org/sites/default/files/2024-10/GEDI-STAT%20brief_formatted_28.10.24_final.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            the ILO,
          </a>{" "}
          in 2023, 708 million women were unable to participate in the workforce
          due to care responsibilities globally. Of these, nearly two-thirds
          (379 million) were in their prime working years, which the ILO defines
          as between the ages of 25 and 54.
        </p>
      </div>
    </div>
  );
};

export default Overview;
